import React from 'react';
import { Truck, Download, Clock, AlertTriangle } from 'lucide-react';
import { ordersAPI } from '../../lib/api';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';

const STATUS_KEYS = {
  pending_payment: 'sales.status_pending_payment', paid: 'sales.status_paid', shipped: 'sales.status_shipped', delivered: 'sales.status_delivered', auto_refunded: 'sales.status_auto_refunded',
};
const STATUS_COLORS = {
  pending_payment: 'bg-yellow-100 text-yellow-700', paid: 'bg-green-100 text-green-700', shipped: 'bg-blue-100 text-blue-700', delivered: 'bg-emerald-100 text-emerald-700', auto_refunded: 'bg-red-100 text-red-700',
};

const getShippingDeadlineInfo = (order, t) => {
  if (order.shipped_at || order.status === 'shipped' || order.status === 'delivered') {
    return { status: 'shipped', text: t('sales.shipped_label'), color: 'text-blue-600' };
  }
  if (order.status === 'auto_refunded') {
    return { status: 'refunded', text: t('sales.refunded_label'), color: 'text-red-600' };
  }
  if (!order.shipping_deadline || order.status !== 'paid') {
    return null;
  }
  
  const now = new Date();
  const deadline = new Date(order.shipping_deadline);
  const diffMs = deadline - now;
  const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));
  
  if (diffDays <= 0) {
    return { status: 'overdue', text: t('sales.deadline_overdue'), color: 'text-red-600', urgent: true };
  } else if (diffDays === 1) {
    return { status: 'urgent', text: t('sales.last_day'), color: 'text-orange-600', urgent: true };
  } else if (diffDays <= 2) {
    return { status: 'warning', text: t('sales.days_left', { days: diffDays }), color: 'text-orange-500' };
  } else {
    return { status: 'ok', text: t('sales.days_to_ship', { days: diffDays }), color: 'text-green-600' };
  }
};

export default function MySalesTab({ sales, setSales }) {
  const { t } = useTranslation();
  const handleGenerateLabel = async (orderId) => {
    try {
      const res = await ordersAPI.generateLabel(orderId);
      if (res.data?.status === 'awaiting_admin_upload') {
        toast.info(res.data.message || t('sales.label_pending_toast'));
      } else {
        toast.success(t('sales.label_generated', { tracking: res.data.tracking_number }));
      }
      const s = await ordersAPI.getMySales();
      setSales(s.data);
    } catch (err) { toast.error(err.response?.data?.detail || t('sales.error')); }
  };

  if (sales.length === 0) return (
    <div className="text-center py-16">
      <Truck className="w-12 h-12 text-[#B8D4E3] mx-auto mb-4" />
      <p className="text-[#5A8BA8] mb-4">{t('sales.no_sales')}</p>
    </div>
  );

  return (
    <div className="space-y-3">
      {sales.map(s => {
        const deadlineInfo = getShippingDeadlineInfo(s, t);
        return (
          <div key={s.id} className="bg-white rounded-xl border border-[#D6E8F0] p-3 sm:p-4" data-testid={`sale-${s.id}`}>
            <div className="flex items-start sm:items-center justify-between gap-2 mb-2">
              <div className="min-w-0">
                <p className="font-semibold text-sm sm:text-base text-[#033871] truncate">{s.model}</p>
                <p className="text-xs text-[#5A8BA8]">{s.buyer_name}</p>
              </div>
              <div className="text-right shrink-0">
                <p className="font-bold text-[#32CD32]">+{s.seller_payout?.toFixed(2) || s.item_price?.toFixed(2)} €</p>
                <span className={`px-2 py-0.5 rounded-full text-[10px] sm:text-xs font-medium ${STATUS_COLORS[s.status]}`}>
                  {t(STATUS_KEYS[s.status], s.status)}
                </span>
              </div>
            </div>
            
            {/* Shipping deadline indicator */}
            {deadlineInfo && s.status === 'paid' && (
              <div className={`flex items-center gap-2 mb-2 text-xs ${deadlineInfo.color}`} data-testid={`shipping-deadline-${s.id}`}>
                {deadlineInfo.urgent ? (
                  <AlertTriangle className="w-4 h-4" />
                ) : (
                  <Clock className="w-4 h-4" />
                )}
                <span className="font-medium">{deadlineInfo.text}</span>
              </div>
            )}
            
            {s.status === 'paid' && !s.shipping_label_url && s.label_status !== 'awaiting_admin_upload' && (
              <Button size="sm" onClick={() => handleGenerateLabel(s.id)} className="bg-[#32CD32] hover:bg-[#28a428] gap-1 mt-2" data-testid={`generate-label-${s.id}`}>
                <Download className="w-3 h-3" /> {t('sales.request_label')}
              </Button>
            )}
            {s.status === 'paid' && !s.shipping_label_url && s.label_status === 'awaiting_admin_upload' && (
              <div className="mt-2 p-2.5 bg-amber-50 border border-amber-200 rounded-lg flex items-center gap-2" data-testid={`label-pending-${s.id}`}>
                <Clock className="w-4 h-4 text-amber-600 shrink-0" />
                <p className="text-xs text-amber-800">
                  {t('sales.label_preparing')}
                </p>
              </div>
            )}
            {(s.shipping_label_url || s.sendcloud_parcel_id || s.tracking_number) && (
              <div className="flex flex-wrap items-center gap-2 mt-2">
                <button
                  onClick={async () => {
                    try {
                      const res = await ordersAPI.downloadLabel(s.id);
                      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
                      const a = document.createElement('a');
                      a.href = url;
                      a.download = `bon-envoi-${s.id.slice(0, 8)}.pdf`;
                      document.body.appendChild(a);
                      a.click();
                      a.remove();
                      window.URL.revokeObjectURL(url);
                    } catch { toast.error(t('sales.error')); }
                  }}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#32CD32] text-white text-xs font-medium hover:bg-[#28a428] transition-colors"
                  data-testid={`download-label-${s.id}`}>
                  <Download className="w-3 h-3" /> {t('sales.download_label')}
                </button>
              </div>
            )}
            {s.tracking_number && (
              <p className="text-xs text-[#5A8BA8] mt-2">
                {t('sales.tracking_num')} <a href={s.tracking_url || `https://www.laposte.fr/outils/suivre-vos-envois?code=${encodeURIComponent(s.tracking_number)}`}
                  target="_blank" rel="noopener noreferrer" className="font-mono text-[#0C63BF] hover:underline" data-testid={`tracking-link-seller-${s.id}`}>{s.tracking_number}</a>
              </p>
            )}
          </div>
        );
      })}
    </div>
  );
}
